/* eslint-disable complexity */
import get from 'lodash/get';

import { Auth0User, CurrentUserCheck } from './types';
import { AuthenticatorUser } from '../types';

const AUTH0_PREFIX = 'auth0|';
export const AUTH0_LS_KEY_PREFIX = '@@auth0spajs@@';

export const AUTH0_COOKIE_PREFIX = 'auth0';

/**
 * Auth0 user id has this format: 'auth0|userId'. Purpose of this method
 * is to remove `auth0|` part from userId
 * @param userId Auth0 user id
 * @param prefix Prefix before auth0 user id
 * @returns Transformed auth0 id as prefix is removed from string
 */
export const transformAuth0UserId = (userId: string, prefix = AUTH0_PREFIX) => {
  if (userId.startsWith(prefix)) {
    return userId.replace(AUTH0_PREFIX, '');
  }
  return userId;
};

/**
 * Determines if current authenticator user is Auth0 user
 * @param user Authenticator user
 * @returns True if user is auth0 (sageID) user
 */
export const isSageIDUser = (user: AuthenticatorUser): boolean => {
  return get(user, 'isSageIDUser', false);
};

/**
 * Determines if current authenticator user is externally managed by SXP
 * @param user Authenticator user
 * @returns True if user is externally managed by SXP
 */
export const isSXPManagedUser = (user: AuthenticatorUser): boolean => {
  return get(user, 'isSXPManagedUser', false);
};

/**
 * Determines if current authenticator user is Auth0 user
 * @param user Authenticator user
 * @returns True if user is auth0 (sageID) user
 */
export const isSetupCompleted = (user: AuthenticatorUser): boolean => {
  return get(user, 'setupCompleted', false);
};

/**
 * Transforms Auth0 user to user object that rest of frontend is expecting
 * @param user Auth0 user
 */
export const transformAuth0User = (user: Auth0User, currentUser: CurrentUserCheck | null) => {
  const userSub = get(user, 'sub', '');
  const userId = transformAuth0UserId(userSub);

  const updatedUser = {
    ...user,
    isSageIDUser: true,
    isSXPManagedUser: currentUser?.is_sxp_managed || false,
    sageIdId: userId,
    username: currentUser?.id || userId,
    sub: currentUser?.id || userId,
    setupCompleted: currentUser?.setup_complete || false,
    attributes: {
      sub: currentUser?.id || userId,
      email_verified: get(user, 'email_verified', false),
      family_name: get(user, 'family_name', ''),
      given_name: get(user, 'given_name', ''),
      email: get(user, 'email', '')
    }
  };

  return updatedUser;
};

export type TransformedAuth0User = ReturnType<typeof transformAuth0User>;

/**
 * Checks if there are any keys with auth0 prefix. If there are some keys with that prefix, that means
 * user was authenticated with Auth0
 * @returns True if user is authenticated with Auth0
 */
export const isAuth0Authenticated = (): boolean => {
  const auth0Keys = Object.keys(localStorage).filter(key => key.includes(AUTH0_LS_KEY_PREFIX));
  return auth0Keys.length > 0;
};

/**
 * Deleting cookies connected to auth0 authentication
 */
export const deleteAuth0Cookies = () => {
  const cookies = document.cookie.split(';');

  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    if (cookieName.startsWith(AUTH0_COOKIE_PREFIX)) {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
};
